import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, AlertTriangle, Info } from 'lucide-react';
import { fmtPct } from '@/components/coveredCall/ccFormat';

const money = (v) => (v == null || Number.isNaN(v)
  ? '—'
  : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/**
 * Withholding tax calculator for the ADR page.
 *
 * Math (all per year, gross of US income tax):
 *   gross            = position × yield
 *   withheld_source  = gross × source_rate      (what the country actually takes)
 *   treaty_portion   = gross × treaty_rate      (what the treaty says you owe)
 *   reclaimable      = withheld_source − treaty_portion
 *   taxable account  → you keep gross − treaty_portion once the excess is
 *                      reclaimed, and the treaty portion becomes a foreign
 *                      tax credit against your US tax
 *   IRA / Roth       → no credit, nothing reclaimable in practice, so you
 *                      keep gross − withheld_source. Canada is 0% by treaty.
 *
 * The Canada branch is intentionally unreachable today: no Canadian row
 * exists in the ADR payload (Canadian NYSE listings are ordinary shares,
 * not ADRs, and their cached yields are distorted by a CAD-dividend /
 * USD-price mismatch). Kept because it is correct if one ever appears —
 * do not "fix" it by admitting Canadian ordinaries.
 */
export default function WithholdingCalculator({ rows }) {
  const [ticker, setTicker] = useState('SHEL');
  const [amount, setAmount] = useState(10000);
  const [account, setAccount] = useState('taxable');

  const options = useMemo(
    () => rows
      .filter((r) => r.dividend_yield != null && r.withholding_rate != null)
      .sort((a, b) => a.ticker.localeCompare(b.ticker)),
    [rows],
  );
  const fund = useMemo(
    () => options.find((r) => r.ticker === ticker) || options[0],
    [options, ticker],
  );

  const calc = useMemo(() => {
    if (!fund) return null;
    const position = Math.max(0, Number(amount) || 0);
    const y = fund.dividend_yield || 0;
    const treatyRate = fund.withholding_rate ?? 0;
    const sourceRate = fund.withholding_source_rate ?? fund.source_rate ?? treatyRate;
    const isCanadaIra = fund.country === 'Canada';

    const gross = position * (y / 100);
    const withheldSource = gross * (sourceRate / 100);
    const treatyPortion = gross * (treatyRate / 100);
    const reclaimable = Math.max(0, withheldSource - treatyPortion);

    if (account === 'ira') {
      const withheld = isCanadaIra ? 0 : withheldSource;
      return {
        position, gross, withheld, reclaimable: 0, credit: 0,
        keep: gross - withheld, lost: withheld,
        effYield: position ? ((gross - withheld) / position) * 100 : null,
        sourceRate, treatyRate, isCanadaIra,
      };
    }
    return {
      position, gross, withheld: withheldSource, reclaimable,
      credit: treatyPortion, keep: gross - treatyPortion, lost: 0,
      effYield: position ? ((gross - treatyPortion) / position) * 100 : null,
      sourceRate, treatyRate, isCanadaIra,
    };
  }, [fund, amount, account]);

  if (!fund || !calc) return null;

  return (
    <section className="mt-12" data-testid="wh-calc">
      <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
        What you'd actually receive
      </h2>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Enter a position size and see the dividend after the issuer country takes its cut — and how
        much of that comes back as a foreign tax credit.
      </p>

      <div className="mt-4 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="p-5 grid sm:grid-cols-3 gap-4 bg-slate-50 dark:bg-slate-900/60">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Holding</span>
            <select
              value={fund.ticker}
              onChange={(e) => setTicker(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              data-testid="wh-calc-ticker"
            >
              {options.map((r) => (
                <option key={r.ticker} value={r.ticker}>
                  {r.ticker} — {r.country} · {fmtPct(r.dividend_yield)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Position size ($)</span>
            <input
              type="number"
              min="0"
              step="500"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 tabular-nums"
              data-testid="wh-calc-amount"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Account type</span>
            <select
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              data-testid="wh-calc-account"
            >
              <option value="taxable">Taxable brokerage</option>
              <option value="ira">IRA / Roth / 401(k)</option>
            </select>
          </label>
        </div>

        <div className="p-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Figure label="Gross dividend / yr" value={money(calc.gross)} testId="wh-calc-gross" />
          <Figure
            label={`Withheld by ${fund.country} (${calc.sourceRate}%)`}
            value={money(calc.withheld)}
            tone={calc.withheld > 0 ? 'bad' : null}
            testId="wh-calc-withheld"
          />
          <Figure
            label={account === 'ira' ? 'Lost for good (no credit)' : 'Foreign tax credit'}
            value={money(account === 'ira' ? calc.lost : calc.credit)}
            tone={account === 'ira' ? 'bad' : 'good'}
            testId="wh-calc-credit"
          />
          <Figure
            label="You keep"
            value={money(calc.keep)}
            hint={calc.effYield != null ? `${fmtPct(calc.effYield)} effective yield` : null}
            tone="good"
            big
            testId="wh-calc-keep"
          />
        </div>

        <div className="px-5 pb-5 space-y-2 text-xs">
          {account === 'taxable' ? (
            <>
              <p className="flex gap-1.5 text-slate-600 dark:text-slate-400">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-cyan-600 dark:text-cyan-400" />
                <span>
                  In a taxable account the {calc.treatyRate}% treaty portion ({money(calc.credit)})
                  becomes a foreign tax credit on Form 1116 — it offsets your US tax on this
                  dividend rather than disappearing, so the real cost is close to zero if you have
                  enough US tax to offset.
                </span>
              </p>
              {calc.reclaimable > 0.005 && (
                <p className="flex gap-1.5 text-amber-700 dark:text-amber-400" data-testid="wh-calc-reclaim">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    {fund.country} withholds {calc.sourceRate}% at source but the treaty rate is
                    only {calc.treatyRate}% — {money(calc.reclaimable)} a year is reclaimable
                    paperwork, not credit. Until you file for it (or your broker applies the treaty
                    rate at source) you'd keep {money(calc.gross - calc.withheld)} instead.
                  </span>
                </p>
              )}
            </>
          ) : (
            <p className="flex gap-1.5 text-rose-700 dark:text-rose-400" data-testid="wh-calc-ira-warning">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                {calc.isCanadaIra
                  ? 'Canada is the exception: the treaty waives withholding entirely for US retirement accounts, so nothing is lost here.'
                  : calc.withheld > 0
                    ? `The foreign tax credit only offsets US tax, and an IRA pays no US tax on the dividend — so this ${money(calc.withheld)} a year is gone permanently, with no reclaim route. That is ${fmtPct(calc.sourceRate, 0)} of your income from this holding.`
                    : `${fund.country} withholds nothing on dividends, so an IRA loses nothing here — one of the few genuinely IRA-friendly foreign payers.`}
              </span>
            </p>
          )}
          <p className="text-slate-500 dark:text-slate-400">
            Estimates using the current yield and standard treaty rates, before US income tax.
            General guidance, not tax advice — see{' '}
            <Link to="/learn/international-dividends" className="text-cyan-700 dark:text-cyan-400 font-semibold hover:underline">the withholding guide</Link>{' '}
            for the paperwork.
          </p>
        </div>
      </div>
    </section>
  );
}

function Figure({ label, value, hint, tone, big, testId }) {
  const cls = tone === 'good'
    ? 'text-emerald-600 dark:text-emerald-400'
    : tone === 'bad'
      ? 'text-rose-600 dark:text-rose-400'
      : 'text-slate-900 dark:text-slate-100';
  return (
    <div className="min-w-0" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`mt-1 font-black tabular-nums truncate ${big ? 'text-2xl' : 'text-xl'} ${cls}`}>{value}</div>
      {hint && <div className="mt-0.5 text-[10px] text-slate-500 dark:text-slate-400">{hint}</div>}
    </div>
  );
}
